(() => {
  'use strict';

  const STORAGE = {
    landing: 'senfon_first_landing',
    referrer: 'senfon_first_referrer',
    utm: 'senfon_first_utm',
    pendingLeadId: 'senfon_pending_lead_id',
    lastSubmittedLeadId: 'senfon_last_submitted_lead_id'
  };

  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const CURRENT_QUERY_KEYS = [...UTM_KEYS, 'product', 'collection'];
  const MAX_VALUE_LENGTH = 200;

  const session = {
    get(key) {
      try { return window.sessionStorage.getItem(key); } catch (_) { return null; }
    },
    set(key, value) {
      try { window.sessionStorage.setItem(key, value); } catch (_) {}
    },
    remove(key) {
      try { window.sessionStorage.removeItem(key); } catch (_) {}
    }
  };

  const trimValue = (value) => String(value || '').slice(0, MAX_VALUE_LENGTH);

  const sanitizeUrl = (rawUrl, allowedQueryKeys = []) => {
    if (!rawUrl) return '';
    try {
      const url = new URL(rawUrl, window.location.origin);
      if (!/^https?:$/.test(url.protocol)) return '';

      const kept = new URLSearchParams();
      allowedQueryKeys.forEach((key) => {
        const value = url.searchParams.get(key);
        if (value) kept.set(key, trimValue(value));
      });

      const query = kept.toString();
      return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
    } catch (_) {
      return '';
    }
  };

  const sanitizeReferrer = (rawUrl) => {
    if (!rawUrl) return '';
    try {
      const url = new URL(rawUrl);
      if (!/^https?:$/.test(url.protocol)) return '';
      return url.origin === window.location.origin ? `${url.origin}${url.pathname}` : url.origin;
    } catch (_) {
      return '';
    }
  };

  const createLeadId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `sf_${window.crypto.randomUUID()}`;
    }
    const random = Math.random().toString(36).slice(2);
    return `sf_${Date.now().toString(36)}_${random}`;
  };

  const getOrCreateLeadId = () => {
    let leadId = session.get(STORAGE.pendingLeadId);
    if (!leadId) {
      leadId = createLeadId();
      session.set(STORAGE.pendingLeadId, leadId);
    }
    return leadId;
  };

  const readFirstTouch = () => {
    const current = sanitizeUrl(window.location.href, CURRENT_QUERY_KEYS);

    let landing = session.get(STORAGE.landing);
    if (!landing) {
      landing = current;
      session.set(STORAGE.landing, landing);
    }

    let referrer = session.get(STORAGE.referrer);
    if (referrer === null) {
      referrer = sanitizeReferrer(document.referrer);
      session.set(STORAGE.referrer, referrer);
    }

    let utm = {};
    const storedUtm = session.get(STORAGE.utm);
    if (storedUtm) {
      try { utm = JSON.parse(storedUtm) || {}; } catch (_) { utm = {}; }
    } else {
      try {
        const landingUrl = new URL(landing);
        UTM_KEYS.forEach((key) => {
          const value = landingUrl.searchParams.get(key);
          if (value) utm[key] = trimValue(value);
        });
      } catch (_) {}
      session.set(STORAGE.utm, JSON.stringify(utm));
    }

    return { landing, referrer, utm, current };
  };

  const upsertHidden = (form, name, value, marker) => {
    let field = form.querySelector(`[data-senfon-attribution="${marker}"]`);
    if (!field) {
      field = form.querySelector(`input[name="${name}"]`);
    }
    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = name;
      form.appendChild(field);
    }
    field.dataset.senfonAttribution = marker;
    field.value = value || '';
    return field;
  };

  const isShopifyContactForm = (form) =>
    !!form.querySelector('input[name="form_type"][value="contact"]');

  const populateForm = (form, touch, leadId) => {
    upsertHidden(form, 'contact[Lead ID]', leadId, 'lead_id');
    upsertHidden(form, 'contact[Landing page]', touch.landing, 'landing_page');
    upsertHidden(form, 'contact[Current page]', touch.current, 'current_page');
    upsertHidden(form, 'contact[Referrer]', touch.referrer, 'referrer');
    upsertHidden(form, 'contact[UTM source]', touch.utm.utm_source || '', 'utm_source');
    upsertHidden(form, 'contact[UTM medium]', touch.utm.utm_medium || '', 'utm_medium');
    upsertHidden(form, 'contact[UTM campaign]', touch.utm.utm_campaign || '', 'utm_campaign');
    upsertHidden(form, 'contact[UTM term]', touch.utm.utm_term || '', 'utm_term');
    upsertHidden(form, 'contact[UTM content]', touch.utm.utm_content || '', 'utm_content');

    if (form.dataset.senfonAttributionReady !== 'true') {
      form.addEventListener('submit', () => {
        const submittedLeadId =
          form.querySelector('[data-senfon-attribution="lead_id"]')?.value ||
          session.get(STORAGE.pendingLeadId) ||
          '';
        if (submittedLeadId) {
          session.set(STORAGE.lastSubmittedLeadId, submittedLeadId);
        }
      });
      form.dataset.senfonAttributionReady = 'true';
    }
  };

  const hasSuccessfulContactSubmission = () =>
    !!document.querySelector(
      '.senfon-quote-form__success, .form-status.form-status-list.form__message'
    );

  const init = () => {
    const touch = readFirstTouch();
    let leadId = getOrCreateLeadId();

    const forms = Array.from(document.forms).filter(isShopifyContactForm);
    forms.forEach((form) => populateForm(form, touch, leadId));

    if (hasSuccessfulContactSubmission()) {
      session.remove(STORAGE.pendingLeadId);
      leadId = getOrCreateLeadId();
      forms.forEach((form) => populateForm(form, touch, leadId));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
